import React, { useState, useEffect } from 'react';

import { computeText } from '@/locales/utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { Text } from '@/components/atoms/Text';
import { SvyButton } from '@/components/savory/SvyButton';
import { confirm } from '@/core/asyncActions/confirm';
import { useLazySalesReportQuery } from '@/services/innovorder/adminTools/adminTools.endpoints';
import { Form } from '@/core/form/Form';
import { useGetBrandRestaurantsQuery } from '@/services/innovorder/brand/brand.endpoints';
import { useParams } from 'react-router';
import { Select } from '@/components/form/Select';
import { Checkbox } from '@/components/atoms/Checkbox';
import { formatDate } from '@/utils/date';
import { LogsContainer, LogLine, Row, Calendar } from './SalesReport.style';
import { FormControl, FormControls } from '../AdminTools.style';

interface ParamTypes {
    brandId: string;
}

export type SalesReportInputs = {
    restaurantId: string;
    dates: {
        startDate: string;
        endDate: string;
    };
};

const SalesReport: React.FunctionComponent = () => {
    const intl = useIntl();
    const { brandId } = useParams<ParamTypes>();
    const [logs, setLogs] = useState<string[]>([]);
    const [isRange, setIsRange] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);

    const [generateSalesReport, { data: stream }] = useLazySalesReportQuery();
    const { data: restaurants, isLoading: isLoadingRestaurants } = useGetBrandRestaurantsQuery({
        brandId: parseInt(brandId),
    });

    const handleSubmitForm = async ({ restaurantId, dates }: SalesReportInputs) => {
        const hasConfirmed = await confirm(computeText(intl, 'adminTools.confirmModal.description'));
        if (hasConfirmed) {
            setLogs([]);
            generateSalesReport({
                restaurantId,
                dates: {
                    startDate: dates.startDate,
                    endDate: isRange ? dates.endDate : dates.startDate,
                },
            });
        }
    };

    const getDate = (date: string | undefined) => {
        return date ? new Date(date) : undefined;
    };

    useEffect(() => {
        if (!stream) return;

        setIsStreaming(true);
        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        let isCancelled = false;

        const read = async () => {
            while (true) {
                const { value, done } = await reader.read();
                if (done || isCancelled) break;

                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split('\n');
                buffer = parts.pop() || '';
                setLogs((prev) => [...prev, ...parts]);
            }
            setIsStreaming(false);
        };

        read();

        return () => {
            isCancelled = true;
            reader.cancel();
            setIsStreaming(false);
        };
    }, [stream]);

    return (
        <Form<SalesReportInputs>>
            {({ handleSubmit, getValues, setValue }) => {
                return (
                    <>
                        <Text type="large" text="brand.adminTools.generateSalesReport.title" />
                        <br />
                        <Text text="brand.adminTools.generateSalesReport.description" />
                        <FormControls>
                            <FormControl>
                                <Row>
                                    <Select
                                        labelId="brand.adminTools.generateSalesReport.form.restaurant"
                                        options={(restaurants || []).map((restaurant) => ({
                                            label: restaurant.name,
                                            value: restaurant.restaurantId,
                                        }))}
                                        name={'restaurantId'}
                                        rules={{ required: computeText(intl, 'field.required') }}
                                        isLoading={isLoadingRestaurants}
                                    />
                                </Row>
                                <Row>
                                    <Checkbox
                                        label="brand.adminTools.generateSalesReport.form.checkbox"
                                        checked={isRange}
                                        onChange={() => setIsRange(!isRange)}
                                    />
                                </Row>
                                <Row>
                                    <Calendar
                                        open={true}
                                        monthShown={1}
                                        inline={true}
                                        selectsRange={isRange}
                                        startDate={getDate(getValues('dates.startDate'))}
                                        endDate={getDate(getValues('dates.endDate'))}
                                        maxDate={new Date()}
                                        onChange={(dates) => {
                                            setValue('dates', {
                                                startDate: formatDate(dates.startDate || '', 'YYYY-MM-DD'),
                                                endDate: formatDate(dates.endDate || '', 'YYYY-MM-DD'),
                                            });
                                        }}
                                    />
                                </Row>
                            </FormControl>

                            {logs.length > 0 && (
                                <LogsContainer>
                                    {logs.map((log, index) => (
                                        <LogLine key={index} isStreaming={isStreaming && index === logs.length - 1}>
                                            {log}
                                        </LogLine>
                                    ))}
                                </LogsContainer>
                            )}

                            <SvyButton onClick={handleSubmit(handleSubmitForm)} disabled={isStreaming}>
                                <FormattedMessage id="button.validate" />
                            </SvyButton>
                        </FormControls>
                    </>
                );
            }}
        </Form>
    );
};

export default SalesReport;
